import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@cian/ui-kit';
import { Microfrontend } from '@cian/mf-react';

import { requestAuthentication } from '../../actions/authentication';
import { BrokerFallbackModal } from '../../components/BrokerFallbackModal';
import { getFirstGaLabel } from '../../selectors/analytics';
import { selectBrokerConsultantBannerButtonText } from '../../selectors/brokerConsultantBanner';
import { getUser, getUserRole } from '../../selectors/user';
import { TTypedThunkDispatch } from '../../types/thunk';
import { useFallbackModalSettings, useMicrofrontAPI, useMicrofrontEvents, useRegion } from './hooks';
import { trackBannerClick } from './tracking';
import { TOpenMicrofrontResultHandler } from './types';
import { IUser } from '../../types/microfrontends/newbuildingBrokerModalsMicroFrontendApi';
import { IUser as IStoreUser, TUserRole } from '../../types/currentUser';

function prepareUser(user: IStoreUser | null, role: TUserRole): IUser {
  if (!user) {
    return {
      phones: null,
      firstName: null,
      isAgent: null,
      isProfi: null,
      isBuilder: null,
    };
  }

  return {
    phones: user.phone ? [{ phone: user.phone.replace(/^\+/, '') }] : null,
    firstName: user.firstName ?? null,
    isAgent: role === 'specialist',
    isProfi: role === 'specialist' || role === 'builder' || role === 'suburbanBuilder',
    isBuilder: role === 'builder' || role === 'suburbanBuilder',
  };
}

export const BrokerConsultantBannerButtonContainer: React.FC = () => {
  const user = useSelector(getUser);
  const role = useSelector(getUserRole);
  const gaLabel = useSelector(getFirstGaLabel);
  const region = useRegion();

  const [isLoading, setIsLoading] = React.useState(false);
  const buttonText = useSelector(selectBrokerConsultantBannerButtonText);
  const fallbackModalSettings = useFallbackModalSettings();

  const [isFallbackModalVisible, setFallbackModalVisibility] = React.useState(false);
  const closeFallbackModal = React.useCallback(() => setFallbackModalVisibility(false), []);
  const showFallbackModal = React.useCallback(() => setFallbackModalVisibility(true), []);

  const dispatch: TTypedThunkDispatch = useDispatch();

  const finishMicrofrontOpening = React.useCallback<TOpenMicrofrontResultHandler>(
    ({ isError }) => {
      if (isError) {
        showFallbackModal();
        setIsLoading(false);
      } else {
        if (isFallbackModalVisible) {
          closeFallbackModal();
        }

        // снятие loading произойдет по событию из микрофронта.
      }
    },
    [isFallbackModalVisible, showFallbackModal, closeFallbackModal],
  );

  const { openMicrofront, microfrontManifest, microfrontRef } = useMicrofrontAPI(finishMicrofrontOpening);

  useMicrofrontEvents(microfrontManifest, setIsLoading);

  // Реф нужен, чтобы получать свежего user в коллбэке во избежание проблемы с замыканием на старое значение.
  const userRef = React.useRef(user);
  const roleRef = React.useRef(role);

  React.useEffect(() => {
    userRef.current = user;
    roleRef.current = role;
  }, [user, role]);

  const startMicrofrontOpening = React.useCallback(() => {
    setIsLoading(true);
    openMicrofront({ user: prepareUser(userRef.current, roleRef.current), region, gaLabel });
  }, [region, gaLabel, openMicrofront]);

  const handleButtonClick = React.useCallback(() => {
    trackBannerClick();

    if (!user) {
      dispatch(
        requestAuthentication({
          onSuccess: () => startMicrofrontOpening,
        }),
      );

      return;
    }

    startMicrofrontOpening();
  }, [dispatch, user, startMicrofrontOpening]);

  return (
    <>
      <Button theme="fill_white_primary" loading={isLoading} onClick={handleButtonClick}>
        {buttonText}
      </Button>
      {isFallbackModalVisible && (
        <BrokerFallbackModal
          {...fallbackModalSettings}
          loading={isLoading}
          onClose={closeFallbackModal}
          onRetry={startMicrofrontOpening}
        />
      )}
      {microfrontManifest && (
        <Microfrontend
          ref={microfrontRef}
          name={microfrontManifest.microfrontendName}
          image={microfrontManifest.imageVersion}
          runtime={microfrontManifest.runtimeName}
          instance={microfrontManifest.instanceId}
        />
      )}
    </>
  );
};
